/**
 * YahooStyleCandlestick — Yahoo-Finance-identical candlestick chart
 * powered by TradingView Lightweight Charts v5 (the same engine that
 * powers Yahoo Finance's own charts).
 *
 * Modes:
 *  1. Standalone: pass `ticker` + `timeframe` + `candleInterval`.
 *     The component fetches its own OHLC from
 *     `/api/stock/indicators-chart/{ticker}` and renders candles.
 *
 *  2. Parent-driven: additionally pass `bars` (the parent's chartData
 *     array containing OHLC + rebased benchmarks + MAs + regression +
 *     VWAP + fair-value + iNAV fields), `visible` (the toggle map),
 *     `overlays` (list of {key,label,color,dasharray,strokeWidth}),
 *     and `levels` (anchor NAV / support / resistance / ATR alert /
 *     ATR stop / warning zone).  Every visible overlay is drawn as a
 *     native TradingView LineSeries on the SAME chart, and horizontal
 *     levels become native price lines.  Toggling ON/OFF adds/removes
 *     the series without re-mounting the chart.
 */
import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import {
  createChart, CandlestickSeries, HistogramSeries, LineSeries, AreaSeries,
  CrosshairMode, LineStyle,
} from "lightweight-charts";
import { RefreshCw } from "lucide-react";
import { API } from "../config/api";

// Parse the backend `time` label into a monotonic UNIX timestamp.
// See MultiBenchmarkChart for why we can't rely on the string directly —
// TradingView requires numeric timestamps for ordering + zooming.
function buildTimeAxis(bars, timeframe) {
  const now = new Date();
  const isIntraday = /^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(bars[0]?.time || "");
  const secPerBar = (() => {
    if (isIntraday) return 60;
    if (timeframe === "5d") return 300;
    return 86400;
  })();
  const baseSec = Math.floor(now.getTime() / 1000) - bars.length * secPerBar;
  return bars.map((b, i) => ({
    ...b,
    _ts: baseSec + i * secPerBar,
    _label: b.time,
  }));
}

// Convert a Recharts `strokeDasharray` string (e.g. "4 2") into
// TradingView's LineStyle enum (Solid / Dashed / Dotted / LargeDashed).
function toLineStyle(dashArray) {
  if (!dashArray) return LineStyle.Solid;
  const parts = String(dashArray).trim().split(/\s+/).map(Number);
  const first = parts[0] || 0;
  if (first >= 6) return LineStyle.LargeDashed;
  if (first >= 3) return LineStyle.Dashed;
  if (first > 0)  return LineStyle.Dotted;
  return LineStyle.Solid;
}

export default function YahooStyleCandlestick({
  ticker,
  timeframe = "1d",
  candleInterval = "auto",
  height = 620,
  // Parent-driven overlay wiring (all optional — component works
  // standalone without any of these).
  bars = null,
  visible = null,
  overlays = null,   // [{ key, label, color, strokeDasharray, strokeWidth }]
  levels = null,     // { anchorNav, support, resistance, atrAlert, atrStop, lastAtr }
  showZones = false, showAtrAlert = false, showAtrStop = false, showAtrZone = false,
  showAnchorNav = false,
  consolidationZones = null,  // [{start_idx, end_idx, upper, lower}] — AI consolidation lines
  percentBase = null, // when set, price labels show % instead of $:
                      // 0 → values are ALREADY percentages; >0 → % change vs this base value
}) {
  const fmtPrice = (p) => {
    if (percentBase == null) return `$${Number(p).toFixed(2)}`;
    const pct = percentBase === 0 ? Number(p) : (Number(p) / percentBase - 1) * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
  };
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const overlaySeriesRef = useRef({});     // { [overlayKey]: LineSeries }
  const priceLinesRef = useRef([]);        // handles of createPriceLine calls
  const zoneSeriesRef = useRef(null);      // AreaSeries handle for ATR band
  const consolSeriesRef = useRef([]);      // LineSeries handles for consolidation zones
  const labelMapRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [hoverBar, setHoverBar] = useState(null);
  const [tick, setTick] = useState(0);
  // Snapshot of the current bar map for the hover pill to enrich with
  // overlay values (EMA / VWAP / iNAV etc at the hovered timestamp).
  const barByTsRef = useRef({});

  // Are we running parent-driven?
  const isParentDriven = Array.isArray(bars) && bars.length > 0;

  // ── Mount chart once ──────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: "#0f172a" },
        textColor: "#cbd5e1",
      },
      grid: {
        vertLines: { color: "#1e293b" },
        horzLines: { color: "#1e293b" },
      },
      rightPriceScale: {
        borderColor: "#334155",
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: "#334155",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (t) => labelMapRef.current[t] || "",
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#94a3b8", width: 1, style: 2, labelBackgroundColor: "#334155" },
        horzLine: { color: "#94a3b8", width: 1, style: 2, labelBackgroundColor: "#334155" },
      },
      localization: {
        timeFormatter: (t) => labelMapRef.current[t] || "",
        priceFormatter: (p) => fmtPrice(p),
      },
      autoSize: true,
    });
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: "#22c55e", downColor: "#ef4444",
      borderUpColor: "#22c55e", borderDownColor: "#ef4444",
      wickUpColor: "#22c55e", wickDownColor: "#ef4444",
      priceLineVisible: true, lastValueVisible: true,
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "vol", color: "#64748b",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
      borderColor: "#334155",
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    // Crosshair-move → surface OHLC + overlay values of the hovered bar
    const onMove = (param) => {
      if (!param || !param.time) { setHoverBar(null); return; }
      const b = param.seriesData.get(candleSeries);
      if (!b) { setHoverBar(null); return; }
      const enrich = barByTsRef.current[param.time] || {};
      setHoverBar({
        label: labelMapRef.current[param.time] || "",
        open: b.open, high: b.high, low: b.low, close: b.close,
        overlays: enrich,
      });
    };
    chart.subscribeCrosshairMove(onMove);
    return () => {
      try { chart.unsubscribeCrosshairMove(onMove); } catch { /* noop */ }
      try { chart.remove(); } catch { /* noop */ }
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      overlaySeriesRef.current = {};
      priceLinesRef.current = [];
      zoneSeriesRef.current = null;
    };
  }, []);

  // ── Data: either from parent's `bars` OR internal fetch ──────────
  const processedBars = useMemo(() => {
    if (!isParentDriven) return null;
    const clean = bars.filter(
      b => b?.candle_open != null && b?.candle_high != null
        && b?.candle_low != null && b?.candle_close != null
    );
    if (!clean.length) return { withTs: [], labels: {}, tsByOriginalTime: {} };
    const withTs = buildTimeAxis(clean, timeframe);
    const labels = {};
    const tsByOriginalTime = {};
    withTs.forEach(b => {
      labels[b._ts] = b._label;
      tsByOriginalTime[b.time] = b._ts;
    });
    return { withTs, labels, tsByOriginalTime };
  }, [bars, timeframe, isParentDriven]);

  // Push candles + volume + summary
  useEffect(() => {
    if (!candleSeriesRef.current) return;
    if (isParentDriven) {
      if (!processedBars) return;
      const { withTs, labels } = processedBars;
      if (!withTs.length) {
        candleSeriesRef.current.setData([]);
        volumeSeriesRef.current.setData([]);
        setSummary(null);
        setError("No OHLC data available for this window.");
        return;
      }
      setError(null);
      const candles = withTs.map(b => ({
        time: b._ts,
        open: b.candle_open, high: b.candle_high,
        low: b.candle_low, close: b.candle_close,
      }));
      const volumes = withTs.map(b => ({
        time: b._ts,
        value: b.volume_raw ?? b.volume ?? 0,
        color: (b.candle_close >= b.candle_open)
          ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)",
      }));
      labelMapRef.current = labels;
      // Build a per-ts lookup so hover pill can show overlay values.
      const byTs = {};
      withTs.forEach(b => { byTs[b._ts] = b; });
      barByTsRef.current = byTs;

      candleSeriesRef.current.setData(candles);
      volumeSeriesRef.current.setData(volumes);
      chartRef.current?.timeScale().fitContent();

      const highs = withTs.map(x => x.candle_high);
      const lows  = withTs.map(x => x.candle_low);
      const first = withTs[0], last = withTs[withTs.length - 1];
      setSummary({
        open: first?.candle_open,
        high: Math.max(...highs),
        low:  Math.min(...lows),
        close: last?.candle_close,
        change: last?.candle_close != null && first?.candle_open != null
          ? last.candle_close - first.candle_open : null,
        changePct: last?.candle_close != null && first?.candle_open != null && first.candle_open !== 0
          ? ((last.candle_close - first.candle_open) / first.candle_open) * 100 : null,
        bars: withTs.length,
      });
      return;
    }
    // Standalone fetch mode
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { timeframe };
        if (candleInterval && candleInterval !== "auto") params.interval = candleInterval;
        const r = await axios.get(`${API}/stock/indicators-chart/${ticker}`, {
          params, timeout: 35000,
        });
        if (cancelled) return;
        const raw = r.data?.chart_data || [];
        const clean = raw.filter(
          b => b?.open != null && b?.high != null && b?.low != null && b?.price != null
        );
        if (!clean.length) {
          setError("No OHLC data available for this window.");
          candleSeriesRef.current.setData([]);
          volumeSeriesRef.current.setData([]);
          setLoading(false);
          return;
        }
        const withTs = buildTimeAxis(clean, timeframe);
        const labels = {};
        withTs.forEach(b => { labels[b._ts] = b.time; });
        const candles = withTs.map(b => ({
          time: b._ts, open: b.open, high: b.high, low: b.low, close: b.price,
        }));
        const volumes = withTs.map(b => ({
          time: b._ts, value: b.volume || 0,
          color: b.price >= b.open ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)",
        }));
        labelMapRef.current = labels;
        barByTsRef.current = Object.fromEntries(withTs.map(b => [b._ts, b]));
        candleSeriesRef.current.setData(candles);
        volumeSeriesRef.current.setData(volumes);
        chartRef.current?.timeScale().fitContent();
        const last = withTs[withTs.length - 1], first = withTs[0];
        setSummary({
          open: first?.open,
          high: Math.max(...withTs.map(x => x.high)),
          low:  Math.min(...withTs.map(x => x.low)),
          close: last?.price,
          change: last?.price != null && first?.open != null ? last.price - first.open : null,
          changePct: last?.price != null && first?.open != null && first.open !== 0
            ? ((last.price - first.open) / first.open) * 100 : null,
          bars: withTs.length,
        });
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load candlestick data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker, timeframe, candleInterval, tick, isParentDriven, processedBars]);

  // ── Overlays (parent-driven only) ────────────────────────────────
  useEffect(() => {
    if (!isParentDriven || !chartRef.current) return;
    if (!processedBars || !overlays) return;
    const { withTs } = processedBars;
    if (!withTs.length) return;
    const chart = chartRef.current;
    const currentKeys = new Set(overlays.filter(o => visible?.[o.key]).map(o => o.key));

    // Remove series whose toggle turned OFF or which are no longer configured
    Object.entries(overlaySeriesRef.current).forEach(([key, series]) => {
      if (!currentKeys.has(key)) {
        try { chart.removeSeries(series); } catch { /* noop */ }
        delete overlaySeriesRef.current[key];
      }
    });

    // Add / update series whose toggle is ON
    overlays.forEach(o => {
      if (!visible?.[o.key]) return;
      // Build data: [{ time, value }] using each bar's field named o.key
      const data = withTs
        .map(b => {
          const v = b[o.key];
          return (v == null || !Number.isFinite(v)) ? null : { time: b._ts, value: v };
        })
        .filter(Boolean);
      if (!data.length) return;
      let s = overlaySeriesRef.current[o.key];
      if (!s) {
        s = chart.addSeries(LineSeries, {
          color: o.color || "#94a3b8",
          lineWidth: o.strokeWidth ? Math.max(1, Math.round(o.strokeWidth)) : 2,
          lineStyle: toLineStyle(o.strokeDasharray),
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: true,
          title: o.label,
        });
        overlaySeriesRef.current[o.key] = s;
      }
      s.setData(data);
    });
  }, [processedBars, overlays, visible, isParentDriven]);

  // ── Horizontal price lines + ATR shaded zone ─────────────────────
  useEffect(() => {
    if (!isParentDriven || !candleSeriesRef.current || !chartRef.current) return;
    const candleSeries = candleSeriesRef.current;
    // Wipe existing lines
    priceLinesRef.current.forEach(pl => {
      try { candleSeries.removePriceLine(pl); } catch { /* noop */ }
    });
    priceLinesRef.current = [];

    if (!levels) return;
    const addLine = ({ price, color, title, dashed = false, weight = 2 }) => {
      if (price == null || !Number.isFinite(price)) return;
      const pl = candleSeries.createPriceLine({
        price,
        color,
        lineWidth: weight,
        lineStyle: dashed ? LineStyle.Dashed : LineStyle.Solid,
        axisLabelVisible: true,
        title,
      });
      priceLinesRef.current.push(pl);
    };

    if (showZones && levels.support != null) {
      addLine({ price: levels.support, color: "#10b981", title: `Support ${fmtPrice(levels.support)}`, dashed: true });
    }
    if (showZones && levels.resistance != null) {
      addLine({ price: levels.resistance, color: "#ef4444", title: `Resistance ${fmtPrice(levels.resistance)}`, dashed: true });
    }
    if (showAtrAlert && levels.atrAlert != null) {
      addLine({ price: levels.atrAlert, color: "#facc15", title: `1× ATR alert ${fmtPrice(levels.atrAlert)}`, dashed: true });
    }
    if (showAtrStop && levels.atrStop != null) {
      addLine({ price: levels.atrStop, color: "#f43f5e", title: `2× ATR stop ${fmtPrice(levels.atrStop)}`, dashed: true });
    }
    if (showAnchorNav && levels.anchorNav != null) {
      addLine({ price: levels.anchorNav, color: "#a78bfa", title: `Anchor NAV ${levels.anchorNav.toFixed(2)}`, dashed: true });
    }

    // Shaded ATR warning zone between 1× and 2× ATR.  v5 doesn't
    // support a native two-value band series, so we approximate it
    // with a filled AreaSeries whose line sits at `upper` and whose
    // fill extends downward (topColor at the line, bottomColor near
    // the price-scale bottom).  The 1× and 2× ATR price lines above
    // clearly mark the band's boundaries.
    if (zoneSeriesRef.current) {
      try { chartRef.current.removeSeries(zoneSeriesRef.current); } catch { /* noop */ }
      zoneSeriesRef.current = null;
    }
    if (showAtrZone && levels.atrAlert != null && levels.atrStop != null && processedBars?.withTs?.length) {
      const upper = Math.max(levels.atrAlert, levels.atrStop);
      const band = chartRef.current.addSeries(AreaSeries, {
        topColor: "rgba(245,158,11,0.22)",
        bottomColor: "rgba(245,158,11,0.02)",
        lineColor: "rgba(245,158,11,0.55)",
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      band.setData(processedBars.withTs.map(b => ({ time: b._ts, value: upper })));
      zoneSeriesRef.current = band;
    }
  }, [levels, isParentDriven, showZones, showAtrAlert, showAtrStop, showAtrZone, showAnchorNav, processedBars]);

  // ── AI consolidation zone lines (upper + lower bound per zone) ────
  useEffect(() => {
    if (!chartRef.current) return;
    consolSeriesRef.current.forEach(s => {
      try { chartRef.current.removeSeries(s); } catch { /* noop */ }
    });
    consolSeriesRef.current = [];
    const withTs = processedBars?.withTs;
    if (!consolidationZones?.length || !withTs?.length) return;
    consolidationZones.forEach(z => {
      const startBar = withTs[Math.max(0, z.start_idx)];
      const endBar = withTs[Math.min(withTs.length - 1, z.end_idx)];
      if (!startBar || !endBar) return;
      [["upper", z.upper], ["lower", z.lower]].forEach(([which, price]) => {
        if (price == null) return;
        const ls = chartRef.current.addSeries(LineSeries, {
          color: "#fb923c",
          lineWidth: 2,
          lineStyle: LineStyle.Dashed,
          priceLineVisible: false,
          lastValueVisible: which === "upper",
          crosshairMarkerVisible: false,
          title: which === "upper" ? "Consolidation" : "",
        });
        ls.setData([
          { time: startBar._ts, value: price },
          { time: endBar._ts, value: price },
        ]);
        consolSeriesRef.current.push(ls);
      });
    });
  }, [consolidationZones, processedBars]);

  const up = summary?.change != null && summary.change >= 0;

  return (
    <div className="relative" data-testid="yahoo-candle-chart">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-mono px-2 py-1.5 border-b border-slate-700 bg-slate-900/60">
        <span className="text-cyan-300 font-bold">{ticker}</span>
        {summary && (
          <>
            <span className="text-slate-400">O <span className="text-slate-100">{fmtPrice(summary.open)}</span></span>
            <span className="text-slate-400">H <span className="text-emerald-300">{fmtPrice(summary.high)}</span></span>
            <span className="text-slate-400">L <span className="text-rose-300">{fmtPrice(summary.low)}</span></span>
            <span className="text-slate-400">C <span className={up ? "text-emerald-300" : "text-rose-300"}>{fmtPrice(summary.close)}</span></span>
            <span className={up ? "text-emerald-300" : "text-rose-300"}>
              {up ? "▲" : "▼"} {summary.change?.toFixed(2)} ({summary.changePct?.toFixed(2)}%)
            </span>
            <span className="text-slate-500">· {summary.bars} bars</span>
          </>
        )}
        {!isParentDriven && (
          <button
            onClick={() => setTick(t => t + 1)}
            data-testid="yahoo-candle-refresh"
            className="ml-auto text-slate-300 hover:text-white flex items-center gap-1"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        )}
      </div>

      {hoverBar && (
        <div
          className="absolute top-10 left-3 z-10 bg-slate-900/95 border border-slate-600 rounded-lg px-3 py-1.5 text-[11px] font-mono shadow-xl pointer-events-none max-w-md"
          data-testid="yahoo-candle-hover"
        >
          <div className="text-slate-400 mb-0.5">{hoverBar.label}</div>
          <div className="flex gap-3 flex-wrap">
            <span className="text-slate-400">O <span className="text-slate-100">${hoverBar.open?.toFixed(2)}</span></span>
            <span className="text-slate-400">H <span className="text-emerald-300">${hoverBar.high?.toFixed(2)}</span></span>
            <span className="text-slate-400">L <span className="text-rose-300">${hoverBar.low?.toFixed(2)}</span></span>
            <span className="text-slate-400">C <span className={hoverBar.close >= hoverBar.open ? "text-emerald-300" : "text-rose-300"}>${hoverBar.close?.toFixed(2)}</span></span>
          </div>
          {/* Overlay readouts for the hovered bar — only visible overlays w/ a numeric value at this ts */}
          {isParentDriven && overlays?.length > 0 && (
            <div className="flex gap-2 flex-wrap mt-1 pt-1 border-t border-slate-700">
              {overlays.filter(o => visible?.[o.key]).map(o => {
                const v = hoverBar.overlays?.[o.key];
                if (v == null || !Number.isFinite(v)) return null;
                return (
                  <span key={o.key} className="whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full inline-block mr-1 align-middle" style={{ backgroundColor: o.color }} />
                    <span className="text-slate-400">{o.label}</span> <span style={{ color: o.color }}>${Number(v).toFixed(2)}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="absolute inset-x-3 top-12 z-10 bg-rose-900/70 border border-rose-600 rounded px-3 py-2 text-xs text-rose-100">
          {error}
        </div>
      )}

      <div
        ref={containerRef}
        style={{ width: "100%", height }}
        className="bg-slate-950"
        data-testid="yahoo-candle-container"
      />
    </div>
  );
}
